import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { cn } from "@/modules/lib/utils"
import { HuBars } from "@/modules/ui/components/charts/HuBars"
import { HuDots } from "@/modules/ui/components/charts/HuDots"
import { HuLines } from "@/modules/ui/components/charts/HuLines"
import { useUser } from "@/modules/users/hooks/useUser"
import { dateFormatByPeriodicity } from "@/modules/utils/dateUtils"
import logoIncrustation from "@images/logo_incrustation.png"
import { Periodicity } from "@modules/completedSessions/completedSessionsType"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useCompletedSessions } from "@modules/profile/publicProfile/hooks/useUserProfile"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { CircleQuestionMark } from "lucide-react"
import { useMemo, useState } from "react"
import { v4 as uuidv4 } from "uuid"

type TrainingInformationProps = {
  className?: string
} & React.ComponentProps<"div">

export function TrainingInformation({ className, ...props }: TrainingInformationProps) {
  const [periodicity, setPeriodicity] = useState<Periodicity>(Periodicity.WEEKLY)

  const limit = useMemo(() => (periodicity === Periodicity.WEEKLY ? 5 : 0), [periodicity])
  const user = useUser()
  const { data: completedSessions, isLoading } = useCompletedSessions(user?.id ?? "", periodicity, limit)
  const language = useLanguage()

  const handlePeriodicity = (selectedPeriodicity: Periodicity) => {
    setPeriodicity(selectedPeriodicity)
  }

  const maxNbSessions = useMemo(
    () => (completedSessions ? Math.max(...completedSessions.map((session) => session.nbSessions)) : 0),
    [completedSessions],
  )

  return (
    <div
      className={cn(
        "relative flex min-w-143 grow flex-col rounded-4xl bg-linear-to-r from-yellow-400 to-yellow-500 p-4",
        className,
      )}
      {...props}
    >
      {/* Buttons */}
      <div className="z-1 flex justify-between">
        <div className="flex h-8 w-full items-center gap-2">
          <PeriodicityButton
            isSelected={periodicity === Periodicity.WEEKLY}
            onClick={() => handlePeriodicity(Periodicity.WEEKLY)}
            text={word("trainingInformation.button.weekly")}
          />
          <PeriodicityButton
            isSelected={periodicity === Periodicity.MONTHLY}
            onClick={() => handlePeriodicity(Periodicity.MONTHLY)}
            text={word("trainingInformation.button.monthly")}
          />
          <PeriodicityButton
            isSelected={periodicity === Periodicity.YEARLY}
            onClick={() => handlePeriodicity(Periodicity.YEARLY)}
            text={word("trainingInformation.button.yearly")}
          />
          <Tooltip>
            <TooltipTrigger render={<CircleQuestionMark className="ml-4 text-textDefault-body" strokeWidth={1} />} />
            <TooltipContent side="bottom" className="w-65">
              <Text fontStyle="BM" color="currentColor" className="text-textDefault-body">
                {word("trainingInformation.button.info")}
              </Text>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      {/* DIAGRAM */}

      <div className="z-1 mt-2 flex h-full flex-col overflow-hidden">
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="flex h-full flex-row items-center justify-center gap-3">
            {!isLoading &&
              (completedSessions && completedSessions.length > 0 ? (
                completedSessions.map((session) => {
                  if (periodicity === Periodicity.WEEKLY) {
                    return (
                      <HuLines
                        key={uuidv4()}
                        number={session.nbSessions}
                        date={dateFormatByPeriodicity({
                          beginDate: session.beginDate,
                          endDate: session.endDate,
                          selectedPeriodicity: periodicity,
                          language,
                        })}
                      />
                    )
                  } else if (periodicity === Periodicity.MONTHLY) {
                    return (
                      <HuBars
                        key={uuidv4()}
                        number={session.nbSessions}
                        heightBar={(session.nbSessions / maxNbSessions) * 100}
                        date={dateFormatByPeriodicity({
                          beginDate: session.beginDate,
                          endDate: session.endDate,
                          selectedPeriodicity: periodicity,
                          language,
                        })}
                      />
                    )
                  } else if (periodicity === Periodicity.YEARLY) {
                    return (
                      <HuDots
                        key={uuidv4()}
                        number={session.nbSessions}
                        date={dateFormatByPeriodicity({
                          beginDate: session.beginDate,
                          endDate: session.endDate,
                          selectedPeriodicity: periodicity,
                          language,
                        })}
                      />
                    )
                  } else {
                    return null
                  }
                })
              ) : (
                <div className="flex items-center justify-center">
                  <Text fontStyle="BS" className="w-2/3 text-center text-textDefault-body">
                    {word("trainingInformation.emptyCompletedSessions")}
                  </Text>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Logo */}
      <div
        className="absolute top-0 right-0 z-0 h-full w-full rounded-4xl bg-contain bg-right bg-no-repeat p-0"
        style={{ backgroundImage: `url(${logoIncrustation})` }}
      ></div>
    </div>
  )
}

function PeriodicityButton({ text, isSelected, onClick }: { text: string; isSelected: boolean; onClick: () => void }) {
  return (
    <div
      className={`${
        isSelected ? "bg-black" : "border border-black hover:bg-primary-600"
      } flex h-full cursor-pointer items-center justify-center rounded-4xl px-3`}
      onClick={onClick}
    >
      <Text fontStyle="BXS" className={cn("text-justify", isSelected ? "text-white" : "text-textDefault-body")}>
        {text}
      </Text>
    </div>
  )
}
